import { useEffect, useState, useMemo } from "react";
import {
  BarChart3, ShoppingCart, Wrench, LifeBuoy, Shield, RefreshCw, Loader2,
} from "lucide-react";
import api from "../lib/api";
import SalesAnalytics from "../components/analytics/SalesAnalytics";
import FieldAnalytics from "../components/analytics/FieldAnalytics";
import SupportAnalytics from "../components/analytics/SupportAnalytics";
import AdminAnalytics from "../components/analytics/AdminAnalytics";

/*
 * Tools → Analytics Hub.
 *
 * Single top-level page with 4 toggleable sections (Sales, Field, Support,
 * Admin). Each section is a focused page of KPI tiles + Recharts
 * visualisations driven by GET /api/analytics/hub?range={7d|30d|90d|180d|365d}.
 *
 * Design: dense left-aligned layout, rose/cyan/violet/amber accents keyed
 * to section meaning (sales=violet, field=amber, support=rose, admin=cyan).
 */

const RANGES = [
  { v: "7d", label: "7 days" },
  { v: "30d", label: "30 days" },
  { v: "90d", label: "90 days" },
  { v: "180d", label: "6 months" },
  { v: "365d", label: "1 year" },
];

const TABS = [
  { key: "sales",   label: "Sales",         Icon: ShoppingCart, color: "#A78BFA" },
  { key: "field",   label: "Field Service", Icon: Wrench,       color: "#F59E0B" },
  { key: "support", label: "Support",       Icon: LifeBuoy,     color: "#F43F5E" },
  { key: "admin",   label: "Admin & Workflows", Icon: Shield,   color: "#1FB6C1" },
];

export default function AnalyticsHub() {
  const [range, setRange] = useState("30d");
  const [active, setActive] = useState("sales");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/analytics/hub?range=${range}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load analytics.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTab = useMemo(() => TABS.find((t) => t.key === active) || TABS[0], [active]);

  return (
    <div className="space-y-5" data-testid="analytics-hub">
      {/* Header */}
      <header className="flex items-start md:items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">Tools</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-3 mt-1">
            <BarChart3 className="w-7 h-7 text-[#1FB6C1]" />
            Analytics
          </h1>
          <p className="text-sm text-[#a0aabf] mt-1 max-w-2xl">
            Real-time view of sales pipeline, field performance, support responsiveness, and admin workflows.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hv-card px-1 py-1 flex items-center gap-1 flex-wrap" data-testid="analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => setRange(r.v)}
                className={`text-xs px-2.5 py-1 rounded transition ${
                  range === r.v ? "bg-[#1fb6c1]/15 text-[#1FB6C1] border border-[#1FB6C1]/40" : "text-[#a0aabf] hover:text-white border border-transparent"
                }`}
                data-testid={`analytics-range-${r.v}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="analytics-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="hv-card p-1 flex items-center gap-1 overflow-x-auto" data-testid="analytics-tabs">
        {TABS.map((t) => {
          const on = t.key === active;
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              className="text-sm px-3 py-2 rounded-md transition shrink-0 flex items-center gap-2"
              style={on ? {
                background: `${t.color}18`,
                border: `1px solid ${t.color}55`,
                color: t.color,
              } : {
                color: "#a0aabf",
                border: "1px solid transparent",
              }}
              data-testid={`analytics-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !data && (
        <div className="hv-card p-8 text-center text-[#a0aabf] text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Crunching numbers…
        </div>
      )}
      {err && (
        <div className="hv-card p-4 text-sm text-rose-300 border-rose-400/40">{err}</div>
      )}

      {data && (
        <div data-testid={`analytics-section-${active}`}>
          {active === "sales" &&   <SalesAnalytics   data={data.sales} />}
          {active === "field" &&   <FieldAnalytics   data={data.field} />}
          {active === "support" && <SupportAnalytics data={data.support} />}
          {active === "admin" &&   <AdminAnalytics   data={data.admin} />}
        </div>
      )}
    </div>
  );
}
